import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  ArrowLeft, ArrowRight, Check, Send, Loader2, Linkedin,
  ExternalLink, GraduationCap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  CAREERS_HERO, CAREERS_COMPANY, CAREERS_WHY, CAREERS_CULTURE,
  CAREERS_INTERNSHIP_TRACKS, CAREERS_GAINS, CAREERS_THRIVES, CAREERS_ROLES
} from "../data/careersData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* ── Hero ── */
const CareersHero = () => (
  <section className="relative min-h-[75vh] flex flex-col justify-center bg-[#2C2A2B] overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#308795]/[0.06] rounded-full -translate-y-1/3 translate-x-1/3" />
    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#7EA13E]/[0.04] rounded-full translate-y-1/3 -translate-x-1/4" />
    <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#E5BD77] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 bg-[#308795]/20 text-[#80d4df] text-sm font-semibold px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-[#308795] rounded-full animate-pulse" />
          {CAREERS_HERO.eyebrow}
        </div>
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-2 max-w-4xl">
        {CAREERS_HERO.headline}
      </h1>
      <p className="text-2xl lg:text-3xl font-bold text-[#E5BD77] mb-5 max-w-3xl">{CAREERS_HERO.headlineSuffix}</p>
      <p className="text-lg lg:text-xl text-[#A9B294] leading-relaxed max-w-2xl mb-10">{CAREERS_HERO.subheadline}</p>
      <div className="flex flex-col sm:flex-row gap-4 mb-12">
        <a href="#apply">
          <Button className="bg-[#308795] hover:bg-[#267580] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#308795]/20 hover:-translate-y-0.5 group">
            Apply Now
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </a>
        <a href="#internships">
          <Button variant="outline" className="border-2 border-white/20 text-white hover:border-[#E5BD77] hover:text-[#E5BD77] font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300">
            View Internships
          </Button>
        </a>
      </div>
      <div className="flex flex-wrap gap-3">
        {CAREERS_COMPANY.map((c, i) => (
          <span key={i} className="text-xs font-medium bg-white/[0.06] border border-white/[0.08] text-[#D5CFC9] px-4 py-2 rounded-lg">{c}</span>
        ))}
      </div>
    </div>
  </section>
);

/* ── Why Work Here ── */
const WhySection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="max-w-2xl mb-14">
      <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Why Asterisk</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Why Work With Asterisk?</h2>
      <p className="text-[#6B6567] text-lg">We combine long-term stability with forward-thinking ambition. That balance is rare.</p>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {CAREERS_WHY.map((w, i) => {
        const Icon = w.icon;
        return (
          <div key={i} className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-7 transition-all duration-300 hover:shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#308795]/[0.08] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#308795]" />
              </div>
              <h3 className="text-lg font-bold text-[#2C2A2B]">{w.title}</h3>
            </div>
            <p className="text-sm text-[#6B6567] leading-relaxed mb-4">{w.description}</p>
            <div className="flex flex-wrap gap-2">
              {w.items.map((item, j) => (
                <span key={j} className="text-xs font-medium bg-[#FAFAF7] border border-[#E0D1BB]/30 text-[#494546] px-3 py-1.5 rounded-lg">{item}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Culture ── */
const CultureSection = () => (
  <Section bg="bg-[#465155]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Our Culture</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">How We Work</h2>
        <p className="text-[#D5CFC9] text-lg leading-relaxed mb-6">We operate like a boutique consultancy with enterprise-grade thinking. No unnecessary bureaucracy. No chaotic experimentation. Just focused, meaningful work.</p>
      </div>
      <div className="mt-8 lg:mt-0 space-y-3">
        {CAREERS_CULTURE.map((c, i) => (
          <div key={i} className="flex items-center gap-4 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-4">
            <Check className="w-5 h-5 text-[#E5BD77] shrink-0" />
            <span className="text-sm font-medium text-[#D5CFC9]">{c}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Internships ── */
const InternshipSection = () => (
  <Section id="internships" bg="bg-white">
    <div className="max-w-2xl mb-14">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Internship Programme</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Shape Your Career Early</h2>
      <p className="text-[#6B6567] text-lg">We strongly believe in nurturing future technology leaders. You won't just "observe." You will build.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
      {CAREERS_INTERNSHIP_TRACKS.map((t, i) => {
        const Icon = t.icon;
        return (
          <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl bg-[#7EA13E]/[0.08] flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-[#7EA13E]" />
            </div>
            <h3 className="text-base font-bold text-[#2C2A2B] mb-3">{t.title}</h3>
            <ul className="space-y-2">
              {t.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#7EA13E] mt-0.5 shrink-0" />
                  <span className="text-xs text-[#6B6567]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
    <div className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-8">
      <h3 className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-4">What You Gain</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CAREERS_GAINS.map((g, i) => (
          <div key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#308795] mt-0.5 shrink-0" />
            <span className="text-sm text-[#494546]">{g}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Who Thrives ── */
const ThrivesSection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Who Thrives Here</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">We Look For Individuals Who Are</h2>
        <p className="text-[#6B6567] text-lg mb-6">You don't need to know everything. You need to be willing to grow.</p>
        <div className="space-y-3">
          {CAREERS_THRIVES.map((t, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-3">
              <Check className="w-4 h-4 text-[#7EA13E] shrink-0" />
              <span className="text-sm font-medium text-[#494546]">{t}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 lg:mt-0 bg-white border border-[#E0D1BB]/30 rounded-2xl p-8">
        <h3 className="text-lg font-bold text-[#2C2A2B] mb-4">Your Career, Built for the Long Term</h3>
        <p className="text-sm text-[#6B6567] leading-relaxed mb-5">Some companies promise rapid growth but offer no foundation. At Asterisk, you gain stability, exposure, mentorship, and a front-row seat to SME digital transformation.</p>
        <div className="space-y-2">
          {["Stability of an established firm", "Exposure to evolving technologies", "Real responsibility early", "Mentorship from experienced leaders"].map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#E5BD77] mt-0.5 shrink-0" />
              <span className="text-sm text-[#494546]">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

/* ── Application Form ── */
const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    website: "",
    linkedin: "",
    role: "",
    valueAnswer: "",
  });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const wordCount = formData.valueAnswer.trim().split(/\s+/).filter(Boolean).length;
  const isOverLimit = wordCount > 150;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.role) {
      toast.error("Please fill in your first name, last name, email, and select a role.");
      return;
    }
    if (!privacyConsent) {
      toast.error("Please agree to the privacy policy to proceed.");
      return;
    }
    if (!formData.linkedin) {
      toast.error("Please provide your LinkedIn profile URL.");
      return;
    }
    if (!formData.valueAnswer.trim()) {
      toast.error("Please answer how you can add value to Asterisk.");
      return;
    }
    if (isOverLimit) {
      toast.error("Your answer exceeds the 150-word limit.");
      return;
    }
    setLoading(true);
    try {
      const message = [
        `Role: ${formData.role}`,
        `LinkedIn: ${formData.linkedin}`,
        formData.company ? `Company: ${formData.company}` : null,
        formData.website ? `Website: ${formData.website}` : null,
        `How can you add value to Asterisk: ${formData.valueAnswer}`,
      ].filter(Boolean).join("\n");

      await axios.post(`${API}/enquiries`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: message,
        selected_path: `Career Application - ${formData.role}`,
      });
      setSubmitted(true);
      toast.success("Application submitted! We'll review it shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Section id="apply" bg="bg-[#2C2A2B]">
        <div className="max-w-lg mx-auto text-center py-12">
          <div className="w-20 h-20 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Application Received!</h2>
          <p className="text-[#A9B294] text-lg">Thank you for your interest in joining Asterisk. We'll review your application and get back to you soon.</p>
        </div>
      </Section>
    );
  }

  return (
    <Section id="apply" bg="bg-[#2C2A2B]">
      <div className="lg:grid lg:grid-cols-5 lg:gap-16 items-start">
        {/* Left */}
        <div className="lg:col-span-2 mb-10 lg:mb-0">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Apply Now</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">Join Us</h2>
          <p className="text-[#A9B294] text-lg leading-relaxed mb-6">
            Whether you are an experienced professional or an ambitious intern shaping your path — we invite you to build the future with us.
          </p>
          <div className="bg-white/[0.06] border border-white/[0.08] rounded-xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Linkedin className="w-5 h-5 text-[#0A66C2]" />
              <span className="text-sm font-bold text-white">LinkedIn Integration</span>
            </div>
            <p className="text-xs text-[#A9B294] leading-relaxed">Share your LinkedIn profile URL so we can learn about your experience and background. We use this to review your professional profile alongside your application.</p>
          </div>
          <div className="space-y-3">
            {["Technology", "Business impact", "Regional exposure", "Innovation discipline", "Long-term growth"].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-[#E5BD77] shrink-0" />
                <span className="text-sm text-[#D5CFC9]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Form */}
        <div className="lg:col-span-3">
          <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">First Name <span className="text-[#E95D54]">*</span></label>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="First name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Last Name <span className="text-[#E95D54]">*</span></label>
                  <input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Last name" />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Email <span className="text-[#E95D54]">*</span></label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="your@email.com" />
              </div>

              {/* Company & Website */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Current or last company" /></div>
                <div><label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="https://www.example.com" /></div>
              </div>

              {/* LinkedIn */}
              <div>
                <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">
                  <span className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-[#0A66C2]" /> LinkedIn Profile <span className="text-[#E95D54]">*</span></span>
                </label>
                <input type="url" value={formData.linkedin} onChange={(e) => setFormData({...formData, linkedin: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#0A66C2] focus:ring-2 focus:ring-[#0A66C2]/10 transition-all" placeholder="https://www.linkedin.com/in/your-profile" />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Role You're Interested In <span className="text-[#E95D54]">*</span></label>
                <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-[#3a3839] text-white text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all appearance-none cursor-pointer">
                  <option value="" className="bg-[#3a3839]">Select a role...</option>
                  {CAREERS_ROLES.map((role, i) => (
                    <option key={i} value={role} className="bg-[#3a3839]">{role}</option>
                  ))}
                </select>
              </div>

              {/* Value Answer */}
              <div>
                <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">
                  How can you add value to Asterisk? <span className="text-[#E95D54]">*</span>
                </label>
                <textarea
                  value={formData.valueAnswer}
                  onChange={(e) => setFormData({...formData, valueAnswer: e.target.value})}
                  rows={5}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 transition-all resize-none ${
                    isOverLimit
                      ? "border-[#E95D54] focus:border-[#E95D54] focus:ring-[#E95D54]/10"
                      : "border-white/10 focus:border-[#308795] focus:ring-[#308795]/10"
                  }`}
                  placeholder="Tell us what unique skills, experiences, or perspectives you'd bring to Asterisk..."
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[10px] text-white/30">Maximum 150 words</p>
                  <p className={`text-xs font-medium ${isOverLimit ? "text-[#E95D54]" : wordCount > 120 ? "text-[#E5BD77]" : "text-white/40"}`}>
                    {wordCount}/150 words
                  </p>
                </div>
              </div>

              {/* Submit */}
              <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
              <Button
                type="submit"
                disabled={loading || isOverLimit || !privacyConsent}
                className="w-full bg-[#308795] hover:bg-[#267580] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#308795]/20 text-base disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                ) : (
                  <>Submit Application<Send className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default function CareersPage() {
  return (
    <>
      <CareersHero />
      <WhySection />
      <CultureSection />
      <InternshipSection />
      <ThrivesSection />
      <ApplicationForm />
    </>
  );
}
